// src/services/standalone-order.service.ts
import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { WarehouseService } from "./warehouse.service";
import type { POTrackingEntry } from "@/data/VendorPOContext";

export type StandaloneOrderStatus = "Submitted" | "Delivered" | "Received";

export interface StandaloneOrder {
  id: string;
  refNumber: string;
  description?: string | null;
  organizationId: string;
  status: StandaloneOrderStatus;
  createdAt: string;
  updatedAt: string;
}

export interface StandaloneOrderLineItem {
  sku: string;
  productName: string;
  quantity: number;
  unitCost: number;
}

export interface StandaloneOrderVendorGroup {
  vendorId: string;
  vendorName: string;
  externalPoNumber?: string;
  trackingNumbers?: POTrackingEntry[];
  items: StandaloneOrderLineItem[];
  tax?: number;  // flat dollar amount, manually entered
}

export interface StandaloneOrderCreateInput {
  description?: string;
  warehouseId?: string | null;
  vendorGroups: StandaloneOrderVendorGroup[];
}

export interface StandaloneOrderPO {
  poId: string;
  poNumber: string;
  vendorId: string;
  vendorName: string;
  status: string;
  subtotal: number;
  tax: number;
  totalCost: number;
  submittedAt?: string;
  deliveredAt?: string;
  confirmedAt?: string;
  externalPoNumber?: string;
  trackingNumbers?: POTrackingEntry[];
  items: StandaloneOrderLineItem[];
  warehouseId?: string | null;
}

export interface StandaloneOrderWithPOs extends StandaloneOrder {
  pos: StandaloneOrderPO[];
}

// Same format as generateDbSafePoNumber() in OrderingQueue.tsx
function generatePoNumber(): string {
  const now = new Date();
  const mmdd =
    String(now.getMonth() + 1).padStart(2, "0") +
    String(now.getDate()).padStart(2, "0");
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `PO-${mmdd}-${rand}`;
}

async function generateRefNumber(orgId: string): Promise<string> {
  const { data } = await supabase
    .from("standalone_orders")
    .select("ref_number")
    .eq("organization_id", orgId)
    .order("ref_number", { ascending: false })
    .limit(1)
    .maybeSingle();

  let nextNum = 1;
  if (data?.ref_number) {
    const match = /(\d+)$/.exec(data.ref_number);
    if (match) nextNum = parseInt(match[1], 10) + 1;
  }
  return `ORD-${String(nextNum).padStart(3, "0")}`;
}

function mapOrder(row: Record<string, unknown>): StandaloneOrder {
  return {
    id: String(row.id ?? ""),
    refNumber: String(row.ref_number ?? ""),
    description: (row.description as string) ?? null,
    organizationId: String(row.organization_id ?? ""),
    status: (row.status as StandaloneOrderStatus) ?? "Submitted",
    createdAt: String(row.created_at ?? ""),
    updatedAt: String(row.updated_at ?? ""),
  };
}

function mapPos(rawPos: unknown[]): StandaloneOrderPO[] {
  return rawPos.map((po) => {
    const p = po as Record<string, unknown>;
    const lineItems = Array.isArray(p.po_line_items)
      ? (p.po_line_items as Array<Record<string, unknown>>).map((li) => ({
          sku: String(li.sku ?? ""),
          productName: String(li.product_name ?? ""),
          quantity: Number(li.quantity_ordered ?? 0),
          unitCost: Number(li.unit_cost ?? 0),
        }))
      : [];
    return {
      poId: String(p.id ?? ""),
      poNumber: String(p.po_number ?? ""),
      vendorId: String(p.vendor_id ?? ""),
      vendorName: String(p.vendor ?? ""),
      status: String(p.status ?? "Submitted"),
      totalCost: Number(p.total_cost ?? 0),
      submittedAt: (p.submitted_at as string) ?? undefined,
      deliveredAt: (p.delivered_at as string) ?? undefined,
      confirmedAt: (p.confirmed_at as string) ?? undefined,
      externalPoNumber: (p.external_po_number as string) ?? undefined,
      trackingNumbers: Array.isArray(p.tracking_numbers)
        ? (p.tracking_numbers as POTrackingEntry[])
        : undefined,
      items: lineItems,
      warehouseId: (p.warehouse_id as string) ?? undefined,
    };
  });
}

export const StandaloneOrderService = {
  async create(input: StandaloneOrderCreateInput): Promise<StandaloneOrderWithPOs | null> {
    const orgId = await getCurrentOrganizationId();
    if (!orgId) return null;

    const refNumber = await generateRefNumber(orgId);
    const now = new Date().toISOString();
    const expectedDelivery = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    // 1. Insert standalone_orders row
    const { data: order, error: orderError } = await supabase
      .from("standalone_orders")
      .insert({
        ref_number: refNumber,
        description: input.description ?? null,
        organization_id: orgId,
        status: "Submitted",
      })
      .select()
      .single();

    if (orderError || !order) {
      console.error("[StandaloneOrderService] Failed to create order:", orderError?.message);
      return null;
    }

    const pos: StandaloneOrderPO[] = [];

    for (const vg of input.vendorGroups) {
      const poNumber = generatePoNumber();
      const subtotal = vg.items.reduce((sum, i) => sum + i.quantity * i.unitCost, 0);
      const tax = vg.tax ?? 0;
      const totalCost = subtotal + tax;

      // 2. Insert purchase_orders (job_id = null, standalone_order_id set)
      const { data: po, error: poError } = await supabase
        .from("purchase_orders")
        .insert({
          organization_id: orgId,
          po_number: poNumber,
          job_id: null,
          job_label: null,
          standalone_order_id: order.id,
          vendor_id: vg.vendorId || null,
          vendor: vg.vendorName,
          status: "Submitted",
          subtotal,
          tax,
          total_cost: totalCost,
          submitted_at: now,
          external_po_number: vg.externalPoNumber ?? null,
          tracking_numbers: vg.trackingNumbers ?? null,
          warehouse_id: input.warehouseId ?? null,
        })
        .select()
        .single();

      if (poError || !po) {
        console.error("[StandaloneOrderService] Failed to create PO for vendor:", vg.vendorName, poError?.message);
        continue;
      }

      // 3. Insert po_line_items
      await supabase.from("po_line_items").insert(
        vg.items.map((item) => ({
          po_id: po.id,
          sku: item.sku,
          product_name: item.productName,
          quantity_ordered: item.quantity,
          unit_cost: item.unitCost,
          line_total: item.quantity * item.unitCost,
        }))
      );

      // 4. Insert shipment (no job context)
      const shipment = await WarehouseService.createShipment({
        poId: po.id,
        poNumber,
        externalPoNumber: vg.externalPoNumber,
        vendorId: vg.vendorId,
        vendorName: vg.vendorName,
        items: vg.items.map((item) => ({
          sku: item.sku,
          productName: item.productName,
          jobId: null,
          jobLabel: null,
          expectedQuantity: item.quantity,
          receivedQuantity: 0,
        })),
        status: "Ordered",
        expectedDelivery,
      });

      // 5. Insert receiving_records (job_id = null — standalone)
      if (shipment) {
        for (const item of vg.items) {
          await WarehouseService.createReceivingRecord({
            poId: po.id,
            poNumber,
            vendorName: vg.vendorName,
            jobId: null,
            jobLabel: null,
            sku: item.sku,
            productName: item.productName,
            expectedQuantity: item.quantity,
            receivedQuantity: 0,
            status: "Pending",
            shipmentId: shipment.id,
            allocationBlocked: false,
            auditEvents: [],
          });
        }
      }

      pos.push({
        poId: po.id,
        poNumber,
        vendorId: vg.vendorId,
        vendorName: vg.vendorName,
        status: "Submitted",
        subtotal,
        tax,
        totalCost,
        submittedAt: now,
        externalPoNumber: vg.externalPoNumber,
        trackingNumbers: vg.trackingNumbers,
        items: vg.items,
      });
    }

    return { ...mapOrder(order as Record<string, unknown>), pos };
  },

  async list(
    page: number,
    pageSize: number,
  ): Promise<{ orders: StandaloneOrderWithPOs[]; count: number }> {
    const orgId = await getCurrentOrganizationId();
    if (!orgId) return { orders: [], count: 0 };

    const from = page * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from("standalone_orders")
      .select("*, purchase_orders(*, po_line_items(*))", { count: "exact" })
      .eq("organization_id", orgId)
      .order("created_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("[StandaloneOrderService] list error:", error.message);
      return { orders: [], count: 0 };
    }

    const orders = (data ?? []).map((row) => {
      const rawPos = Array.isArray((row as Record<string, unknown>).purchase_orders)
        ? ((row as Record<string, unknown>).purchase_orders as unknown[])
        : [];
      return { ...mapOrder(row as Record<string, unknown>), pos: mapPos(rawPos) };
    });

    return { orders, count: count ?? 0 };
  },

  async getById(id: string): Promise<StandaloneOrderWithPOs | null> {
    const { data, error } = await supabase
      .from("standalone_orders")
      .select("*, purchase_orders(*, po_line_items(*))")
      .eq("id", id)
      .single();

    if (error || !data) return null;

    const rawPos = Array.isArray((data as Record<string, unknown>).purchase_orders)
      ? ((data as Record<string, unknown>).purchase_orders as unknown[])
      : [];
    return { ...mapOrder(data as Record<string, unknown>), pos: mapPos(rawPos) };
  },
};
